import { useState } from 'react';

import { useGetAccounts, useGetFormalOffers } from '@api/lendingAPI/lapiHooks';
import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';

import { getOfferSetType } from './getOfferSetType';
import { MoreFundsOnly } from './MoreFundsOnly/MoreFundsOnly';
import { MoreFundsWithRepaymentChoice } from './MoreFundsWithRepaymentChoice/MoreFundsWithRepaymentChoice';
import { LoadingSkeleton } from './PageContainer';
import { RepaymentReductionOnly } from './RepaymentReductionOnly/RepaymentReductionOnly';
import { RepaymentReductionWithChoice } from './RepaymentReductionWithChoice/RepaymentReductionWithChoice';
import FallbackErrorModal from '../../../../modals/fallbackErrorModal/FallbackErrorModal';
import { getTimeOutDate } from '../../../../myOffers/MyOffers.helpers';

export const Approved = ({ accountId }: { accountId: string }) => {
    const { formalOffers, formalOffersError, loadingFormalOffers } =
        useGetFormalOffers();
    const {
        data: accountsResponse,
        isLoading: accountsLoading,
        isError: accountsError
    } = useGetAccounts();

    const [selectedOffer, setSelectedOffer] = useState<TFormalOffer | null>(
        null
    );
    const [selectedAmount, setSelectedAmount] = useState<number | null>(null);

    if (loadingFormalOffers || accountsLoading) {
        return <LoadingSkeleton data-testid="approved-loading-skeleton" />;
    }
    if (formalOffersError || accountsError) return <FallbackErrorModal />;

    const account = accountsResponse.accounts.find(
        (account) => account.account_id === accountId
    )!;
    const loanOfferId = account.loan_offer_id!;
    const offerSetType = getOfferSetType(formalOffers, loanOfferId);
    const timeoutDate = getTimeOutDate(formalOffers);
    const outstandingBalance = account!.pending_balance!.total!;

    switch (offerSetType) {
        case 'more_funds_only':
            return (
                <MoreFundsOnly
                    formalOffers={formalOffers}
                    timeoutDate={timeoutDate}
                    offerSetType={offerSetType}
                    selectedAmount={selectedAmount!}
                    setSelectedAmount={setSelectedAmount}
                    outstandingBalance={outstandingBalance}
                    loanOfferId={loanOfferId}
                />
            );
        case 'more_funds_with_repayment_choice':
            return (
                <MoreFundsWithRepaymentChoice
                    timeoutDate={timeoutDate}
                    offerSetType={offerSetType}
                    formalOffers={formalOffers}
                    selectedOffer={selectedOffer}
                    setSelectedOffer={setSelectedOffer}
                    selectedAmount={selectedAmount}
                    setSelectedAmount={setSelectedAmount}
                    outstandingBalance={outstandingBalance}
                />
            );
        case 'repayment_reduction_only':
            return (
                <RepaymentReductionOnly
                    timeoutDate={timeoutDate}
                    offerSetType={offerSetType}
                    formalOffer={formalOffers[0]}
                    outstandingBalance={outstandingBalance}
                />
            );
        case 'repayment_reduction_with_choice':
            return (
                <RepaymentReductionWithChoice
                    timeoutDate={timeoutDate}
                    offerSetType={offerSetType}
                    formalOffers={formalOffers}
                    selectedOffer={selectedOffer}
                    setSelectedOffer={setSelectedOffer}
                    outstandingBalance={outstandingBalance}
                />
            );
        default:
            // TODO add logging for this case
            return <div>Error, unknown offer set</div>;
    }
};
